import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Edit, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { COLOR_MAP, PALETTE, colorOf } from "./contractColors";

const ColorPicker = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-1.5">
    {PALETTE.map((c) => (
      <button
        key={c}
        type="button"
        onClick={() => onChange(c)}
        title={c}
        className={`w-6 h-6 rounded-full ${COLOR_MAP[c].dot} transition-transform hover:scale-110 ${value === c ? "ring-2 ring-offset-2 ring-slate-700" : ""}`}
        data-testid={`color-${c}`}
      />
    ))}
  </div>
);

export const ContractCategoriesDialog = ({ open, onOpenChange, onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newColor, setNewColor] = useState("blue");
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ label: "", color: "blue" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.getContractCategories();
      setItems(data.items || []);
    } catch (_e) {
      toast.error("Erreur chargement catégories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const handleCreate = async () => {
    if (!newLabel.trim()) return toast.error("Nom obligatoire");
    try {
      await api.createContractCategory({ label: newLabel.trim(), color: newColor });
      toast.success("Catégorie créée");
      setNewLabel("");
      setNewColor("blue");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const handleSaveEdit = async (id) => {
    try {
      await api.updateContractCategory(id, editForm);
      toast.success("Catégorie mise à jour");
      setEditingId(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Supprimer cette catégorie ? Les contrats associés passeront en 'Autre'.")) return;
    try {
      await api.deleteContractCategory(id);
      toast.success("Supprimée");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]" data-testid="categories-dialog">
        <DialogHeader>
          <DialogTitle>Catégories de charges</DialogTitle>
          <DialogDescription>Personnalisez les catégories pour classer vos contrats. Les couleurs sont reprises sur le graphique.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[420px] overflow-auto pr-1">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : (
            items.map((cat) => {
              const editing = editingId === cat.id;
              return (
                <div key={cat.id} className="flex items-center gap-2 p-2 rounded-lg border bg-white">
                  {editing ? (
                    <>
                      <div className="flex flex-col gap-1.5 flex-1">
                        <Input
                          value={editForm.label}
                          onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                          className="h-8"
                        />
                        <ColorPicker value={editForm.color} onChange={(c) => setEditForm({ ...editForm, color: c })} />
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => handleSaveEdit(cat.id)} data-testid={`save-cat-${cat.id}`}>
                        <Check className="h-4 w-4 text-emerald-600" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Badge className={`${colorOf(cat.color).badge} border`}>{cat.label}</Badge>
                      {cat.is_default && <span className="text-[10px] text-muted-foreground">par défaut</span>}
                      <div className="flex-1" />
                      <Button size="sm" variant="ghost" onClick={() => { setEditingId(cat.id); setEditForm({ label: cat.label, color: cat.color }); }}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(cat.id)} data-testid={`del-cat-${cat.id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="border-t pt-3 space-y-2">
          <Label className="text-sm">Nouvelle catégorie</Label>
          <div className="flex gap-2">
            <Input
              placeholder="Ex: Logiciels SaaS"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              data-testid="new-cat-name"
            />
            <Button onClick={handleCreate} data-testid="add-category-btn">
              <Plus className="h-4 w-4 mr-1" />Ajouter
            </Button>
          </div>
          <ColorPicker value={newColor} onChange={setNewColor} />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContractCategoriesDialog;
